import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { ShieldCheck, Users, FileEdit, ClipboardList, FlaskConical, Video, ShieldAlert, GraduationCap, HelpCircle, Phone, TrendingUp, Pin, Languages, Newspaper, Mail, Handshake, ShoppingBag , Ticket, Share2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin console layout: left rail with three sections, content area
 * rendered via <Outlet />. Routes are configured in App.js under /admin/*.
 */
export default function AdminShell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pendingMentorCalls, setPendingMentorCalls] = useState(0);

  // Poll the mentor-call pending count so admins see a red badge without
  // having to open the section. Refreshes every 30s while the shell is
  // mounted — cheap because the endpoint is a single count_documents.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    const load = () => api.get("/admin/mentor-call/pending-count")
      .then((r) => { if (!cancelled) setPendingMentorCalls(r.data.count || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const NAV_GROUPS = [
    { title: "Members", items: [
      { to: "/admin", label: t("admin.nav.compliance"), icon: ShieldCheck, end: true, testId: "admin-nav-compliance" },
      { to: "/admin/users", label: t("admin.nav.users"), icon: Users, testId: "admin-nav-users" },
      { to: "/admin/onboarding", label: "Onboarding", icon: GraduationCap, testId: "admin-nav-onboarding" },
      { to: "/admin/security", label: "Security", icon: ShieldAlert, testId: "admin-nav-security" },
    ] },
    { title: "Learning", items: [
      { to: "/admin/content", label: t("admin.nav.content"), icon: FileEdit, testId: "admin-nav-content" },
      { to: "/admin/quizzes", label: "Quizzes", icon: HelpCircle, testId: "admin-nav-quizzes" },
      { to: "/admin/webinars", label: "Video Library", icon: Video, testId: "admin-nav-webinars" },
      { to: "/admin/mentor-calls", label: "Mentor calls", icon: Phone, testId: "admin-nav-mentor-calls", badge: pendingMentorCalls },
    ] },
    { title: "Discord", items: [
      { to: "/admin/trade-setups", label: "Trade Setups", icon: TrendingUp, testId: "admin-nav-trade-setups" },
      { to: "/admin/channel-guides", label: "Channel Guides", icon: Pin, testId: "admin-nav-channel-guides" },
      { to: "/admin/channel-mirrors", label: "Channel Mirrors", icon: Languages, testId: "admin-nav-channel-mirrors" },
      { to: "/admin/recaps", label: "Recaps", icon: Newspaper, testId: "admin-nav-recaps" },
    ] },
    { title: "Revenue", items: [
      { to: "/admin/shop", label: "Shop", icon: ShoppingBag, testId: "admin-nav-shop" },
      { to: "/admin/promotions", label: "Promotions", icon: Ticket, testId: "admin-nav-promotions" },
      { to: "/admin/partners", label: "Partners", icon: Handshake, testId: "admin-nav-partners" },
      { to: "/admin/social-links", label: "Social Links", icon: Share2, testId: "admin-nav-social-links" },
    ] },
    { title: "Residency", items: [
      { to: "/admin/applications", label: "Applications", icon: FlaskConical, testId: "admin-nav-applications" },
      { to: "/admin/residency-welcome", label: "Welcome Pack", icon: Mail, testId: "admin-nav-residency-welcome" },
    ] },
  ];

  return (
    <div data-testid={ADMIN.root} className="grid grid-cols-1 lg:grid-cols-[180px_minmax(0,1fr)] gap-5 -ml-2 lg:-ml-4">
      <aside className="lg:sticky lg:top-6 lg:self-start">
        <nav className="flex flex-row lg:flex-col gap-1 lg:gap-5 overflow-x-auto">
          {NAV_GROUPS.map((g) => (
          <div key={g.title} data-testid={`admin-nav-group-${g.title.toLowerCase()}`} className="flex flex-row lg:flex-col gap-1 shrink-0">
          <div className="hidden lg:block px-3 mb-1 text-[10px] uppercase tracking-[0.24em] text-amber-600 font-semibold">{g.title}</div>
          {g.items.map(({ to, label, icon: Icon, end, testId, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-blue-800 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={15} />
              <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span
                  data-testid={`${testId}-badge`}
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold"
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
          ))}
          </div>
          ))}
        </nav>
        <div className="hidden lg:block mt-8 text-[11px] text-slate-400 leading-relaxed">
          <ClipboardList size={12} className="inline mr-1" />
          Everything here is audit-logged.
        </div>
      </aside>

      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
